import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  Box,
  useTheme,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import dayjs from "dayjs";
import { minMaxYmdFromList, parseYmdBoundary } from "../utility";
import SignalCalendarDayDetailDialog from "./SignalCalendarDayDetailDialog";

/** Min height for 6 week rows; day cells use equal flex columns so Sun–Sat stay aligned */
const DAY_ROW_HEIGHT_PX = 40;
const SLIDE_MIN_HEIGHT_PX = (DAY_ROW_HEIGHT_PX + 4) * 6;

export default function SignalTradingDaysCalendarDialog({
  open,
  onClose,
  highlightedYmds,
  signalsByCalendarYmd = {},
  periodMinYmd,
  periodMaxYmd,
  strategyName = "",
}) {
  const theme = useTheme();
  const ymdSet = useMemo(() => new Set(highlightedYmds), [highlightedYmds]);

  const { min: signalMinYmd, max: signalMaxYmd } = useMemo(
    () => minMaxYmdFromList(highlightedYmds),
    [highlightedYmds]
  );

  const calendarMinYmd = useMemo(
    () => signalMinYmd || periodMinYmd || "",
    [signalMinYmd, periodMinYmd]
  );
  const calendarMaxYmd = useMemo(
    () => signalMaxYmd || periodMaxYmd || "",
    [signalMaxYmd, periodMaxYmd]
  );

  const minDate = useMemo(() => {
    const d = parseYmdBoundary(calendarMinYmd);
    return d?.isValid() ? d : undefined;
  }, [calendarMinYmd]);

  const maxDate = useMemo(() => {
    const d = parseYmdBoundary(calendarMaxYmd);
    return d?.isValid() ? d : undefined;
  }, [calendarMaxYmd]);

  const [referenceDate, setReferenceDate] = useState(() => dayjs().startOf("month"));
  const [calendarMountKey, setCalendarMountKey] = useState(0);
  const wasOpenRef = useRef(false);
  const prevEndAnchorYmdRef = useRef("");

  useEffect(() => {
    if (!open) {
      wasOpenRef.current = false;
      return;
    }

    let anchor = parseYmdBoundary(calendarMaxYmd);
    if (!anchor?.isValid()) {
      anchor = dayjs();
    }
    const viewMonth = anchor.startOf("month");
    setReferenceDate(viewMonth);

    const firstOpen = !wasOpenRef.current;
    const anchorChanged = prevEndAnchorYmdRef.current !== calendarMaxYmd;
    if (firstOpen || anchorChanged) {
      setCalendarMountKey((k) => k + 1);
    }
    wasOpenRef.current = true;
    prevEndAnchorYmdRef.current = calendarMaxYmd;
  }, [open, calendarMaxYmd]);

  const [detailYmd, setDetailYmd] = useState(null);

  useEffect(() => {
    if (!open) setDetailYmd(null);
  }, [open]);

  const handleSignalDayClick = useCallback((ymd) => {
    setDetailYmd(ymd);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailYmd(null);
  }, []);

  const handleCloseAll = useCallback(() => {
    setDetailYmd(null);
    onClose();
  }, [onClose]);

  const clampViewToPeriod = useCallback(
    (d) => {
      let m = dayjs(d).startOf("month");
      if (minDate && m.isBefore(minDate, "month")) {
        m = minDate.startOf("month");
      }
      if (maxDate && m.isAfter(maxDate, "month")) {
        m = maxDate.startOf("month");
      }
      return m;
    },
    [minDate, maxDate]
  );

  const handleMonthChange = useCallback(
    (d) => setReferenceDate(clampViewToPeriod(d)),
    [clampViewToPeriod]
  );

  const handleYearChange = useCallback(
    (d) => setReferenceDate(clampViewToPeriod(d)),
    [clampViewToPeriod]
  );

  const shouldDisableMonth = useCallback(
    (month) => {
      const m = dayjs(month).startOf("month");
      if (minDate && m.isBefore(minDate, "month")) return true;
      if (maxDate && m.isAfter(maxDate, "month")) return true;
      return false;
    },
    [minDate, maxDate]
  );

  const primary = theme.palette.primary.main;

  const DaySlot = useCallback(
    (props) => {
      const { day, outsideCurrentMonth, onClick, ...other } = props;
      const ymd = day.format("YYYY-MM-DD");
      const isSignalDay = ymdSet.has(ymd);
      const label = day.format("D");

      const textColor = isSignalDay
        ? primary
        : outsideCurrentMonth
          ? theme.palette.text.disabled
          : theme.palette.text.secondary;

      const handleClick = (e) => {
        if (!isSignalDay) {
          onClick?.(e);
          return;
        }
        e.preventDefault();
        e.stopPropagation();
        handleSignalDayClick(ymd);
      };

      return (
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={outsideCurrentMonth}
          disabled={!isSignalDay}
          onClick={handleClick}
          disableMargin
          disableRipple={!isSignalDay}
          disableTouchRipple={!isSignalDay}
          disableHighlightToday
          sx={{
            flex: "1 1 0",
            minWidth: 0,
            width: "auto",
            maxWidth: "none",
            height: DAY_ROW_HEIGHT_PX,
            p: 0,
            m: 0,
            borderRadius: 0,
            backgroundColor: "transparent !important",
            boxShadow: "none !important",
            "&.Mui-focusVisible": {
              backgroundColor: isSignalDay ? "action.selected" : "transparent !important",
            },
            ...(isSignalDay
              ? {
                  pointerEvents: "auto",
                  cursor: "pointer",
                  "&:hover": { backgroundColor: "action.hover" },
                }
              : {
                  pointerEvents: "none",
                  "&:hover": { backgroundColor: "transparent !important" },
                  "&.Mui-disabled": {
                    opacity: 1,
                    backgroundColor: "transparent !important",
                  },
                }),
          }}
        >
          <Box
            component="span"
            sx={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: "1.5em",
              fontSize: "0.8125rem",
              fontWeight: isSignalDay ? 600 : 400,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1,
              letterSpacing: "0.01em",
              color: `${textColor} !important`,
              ...(isSignalDay && {
                borderBottom: `2px solid ${primary}`,
                paddingBottom: "3px",
                marginBottom: "-3px",
              }),
            }}
          >
            {label}
          </Box>
        </PickersDay>
      );
    },
    [theme, ymdSet, primary, handleSignalDayClick]
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="xs"
        fullWidth
        PaperProps={{
          elevation: 0,
          sx: {
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1,
            py: 1.75,
            px: 2,
            fontSize: "0.9375rem",
            fontWeight: 600,
            letterSpacing: "0.01em",
          }}
        >
          Signal Sets
          <IconButton
            size="small"
            onClick={onClose}
            aria-label="Close calendar"
            sx={{ color: "text.secondary" }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <Divider />
        <DialogContent
          sx={{
            p: 0,
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
            alignItems: "stretch",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DateCalendar
              key={calendarMountKey}
              referenceDate={referenceDate}
              value={null}
              onChange={() => {}}
              onMonthChange={handleMonthChange}
              onYearChange={handleYearChange}
              minDate={minDate}
              maxDate={maxDate}
              shouldDisableMonth={shouldDisableMonth}
              disableHighlightToday
              showDaysOutsideCurrentMonth
              sx={{
                width: "100%",
                maxWidth: "none",
                m: 0,
                p: 0,
                px: 0.5,
                bgcolor: "transparent",
                border: "none",
                borderRadius: 0,
                boxShadow: "none",
                overflow: "visible",
                "& .MuiDayCalendar-slideTransition, & .MuiPickersSlideTransition-root":
                  {
                    overflow: "visible",
                    minHeight: `${SLIDE_MIN_HEIGHT_PX}px`,
                  },
                "& .MuiDayCalendar-monthContainer": {
                  width: "100%",
                },
                "& .MuiDayCalendar-weekContainer": {
                  margin: 0,
                  width: "100%",
                  display: "flex",
                  justifyContent: "stretch",
                },
                "& .MuiDayCalendar-header": {
                  display: "flex",
                  width: "100%",
                  justifyContent: "stretch",
                  paddingLeft: 0,
                  paddingRight: 0,
                  marginBottom: 0.5,
                },
                "& .MuiDayCalendar-weekDayLabel": {
                  flex: "1 1 0",
                  minWidth: 0,
                  width: "auto",
                  maxWidth: "none",
                  height: 28,
                  margin: 0,
                  padding: 0,
                  fontSize: "0.6875rem",
                  fontWeight: 600,
                  color: "text.secondary",
                  letterSpacing: "0.04em",
                },
              }}
              slotProps={{
                calendarHeader: {
                  sx: {
                    marginTop: 0,
                    marginBottom: 0.5,
                    px: 1,
                    maxHeight: 44,
                    minHeight: 44,
                    "& .MuiPickersCalendarHeader-labelContainer": {
                      ...theme.typography.subtitle2,
                      fontWeight: 600,
                      letterSpacing: "0.02em",
                    },
                  },
                },
              }}
              slots={{ day: DaySlot }}
            />
          </LocalizationProvider>
        </DialogContent>
      </Dialog>

      <SignalCalendarDayDetailDialog
        open={Boolean(detailYmd)}
        onBack={handleCloseDetail}
        onCloseAll={handleCloseAll}
        detailYmd={detailYmd}
        strategyName={strategyName}
        signalsByCalendarYmd={signalsByCalendarYmd}
      />
    </>
  );
}
